import React, { useState, useEffect, useRef } from 'react';

// Import glyph paths
import { glyphPaths } from '../icons/icon-paths';
// Import the grid index data
import gridIndexData from '../data/grid-index.json';

// Animation Configuration
const ANIMATION_CONFIG = {
  speed: 900, // Animation duration in milliseconds (lower = faster) - increased from 250 to 500 for slower animation
  frequency: 2000, // Random animation frequency in milliseconds (lower = more frequent) - changed to 1 second
  enabled: true // Enable/disable automatic random animations
};

// Pixelation Configuration
const PIXELATION_CONFIG = {
  minPixelSize: 2, // Minimum pixel size (least pixelated)
  maxPixelSize: 8, // Maximum pixel size (most pixelated)
  pixelStepSize: 2, // Step size for pixelation (2 = 2,4,6,8,10...)
  pixelSteps: 8 // Number of steps from min to max
};

// Glyph Configuration
const GLYPH_CONFIG = {
  size: 10, // Glyph size in pixels
  padding: 5, // Padding from edges
  scale: 24 // Original glyph design size (for scaling)
};

// Debug Configuration
const DEBUG_CONFIG = {
  showDebugText: true, // Show debug text on images
  showClickIndicator: false, // Show click indicator
  debugTextColor: 'rgba(255, 255, 255, 1)', // Debug text color
  clickIndicatorColor: '#3520D6' // Click indicator color
};

const ImageGridCanvas = () => {
  const canvasRef = useRef(null);
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [animations, setAnimations] = useState({});
  const [imageFolders, setImageFolders] = useState({});
  const [largeBlock, setLargeBlock] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [lastClick, setLastClick] = useState(null); // Track last click for debugging
  const [hoverEnabled, setHoverEnabled] = useState(true); // Toggle hover animations - enabled by default
  const [currentHoveredCell, setCurrentHoveredCell] = useState(null); // Track current hovered cell
  const [hoveredCells, setHoveredCells] = useState(new Set()); // Track cells that have been hovered
  const [renderCounter, setRenderCounter] = useState(0);
  const [currentDisplayedImages, setCurrentDisplayedImages] = useState({});
  const [cellCodes, setCellCodes] = useState({});
  const [largeBlockCodes, setLargeBlockCodes] = useState({});

  // Generate 4-character hash code from input string
  const generateHashCode = (input) => {
    // Simple hash function to generate consistent 4-char codes
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to positive number and get 4 characters
    const positiveHash = Math.abs(hash);
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    
    // Generate 4 characters from the hash
    for (let i = 0; i < 4; i++) {
      const index = (positiveHash >> (i * 8)) & 0x3F; // 6 bits per character (64 possible chars)
      result += chars[index % chars.length];
    }
    
    return result;
  };

  // Responsive grid configuration
  const getGridConfig = () => {
    let cols = 12;
    if (windowWidth < 768) cols = 6; // Mobile
    else if (windowWidth < 1024) cols = 8; // Tablet
    else if (windowWidth < 1440) cols = 10; // Small desktop
    
      return {
        rows: 4,
      cols: cols,
      gap: 0 // No gap to fill entire space
    };
  };

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Grid configuration
  const GRID_CONFIG = getGridConfig();

  // Load images using the grid-index.json data
  useEffect(() => {
    const loadImages = async () => {
      try {
        setIsLoading(true);
        
        console.log('Loading images from grid-index.json:', gridIndexData);
        
        // Generate the complete image sequence from the grid index data
        const imageSequence = [];
        let imageId = 0;
        
        // Iterate through the structure to build the image sequence
        Object.entries(gridIndexData.structure).forEach(([topFolder, subfolders]) => {
          Object.entries(subfolders).forEach(([subfolder, folderData]) => {
            folderData.files.forEach(filename => {
              const imagePath = `/img/complete_grid/${topFolder}/${subfolder}/${filename}`;
              const imageNumber = filename.replace('.avif', '');
              
              // Create image object
              const img = new window.Image();
              img.crossOrigin = 'anonymous';
              
              // We'll push the image object after it loads
              img.onload = () => {
                imageSequence.push({
                  img,
                  imageId: imageId++,
                  topFolder,
                  subfolder,
                  number: imageNumber,
                  path: imagePath,
                  uniqueId: `${topFolder}_${subfolder}_${imageNumber}` // For folder cycling
                });
                // If this is the last image, set the state
                if (imageSequence.length === gridIndexData.totalImages) {
                  // --- BEGIN: ALTERNATING LOGIC ---
                  // Separate men and women images
                  const menImages = imageSequence.filter(img => img.topFolder === 'men');
                  const womenImages = imageSequence.filter(img => img.topFolder === 'women');
                  // Alternate man, woman, man, woman, ...
                  const alternatingImages = [];
                  let menIdx = 0, womenIdx = 0;
                  let useMan = true;
                  while (menIdx < menImages.length || womenIdx < womenImages.length) {
                    if (useMan && menIdx < menImages.length) {
                      alternatingImages.push(menImages[menIdx++]);
                    } else if (!useMan && womenIdx < womenImages.length) {
                      alternatingImages.push(womenImages[womenIdx++]);
                    }
                    useMan = !useMan;
                  }
                  // If more cells than unique images, repeat alternation
                  const totalNeeded = GRID_CONFIG.rows * GRID_CONFIG.cols;
                  while (alternatingImages.length < totalNeeded) {
                    menIdx = 0; womenIdx = 0; useMan = true;
                    while (alternatingImages.length < totalNeeded && (menIdx < menImages.length || womenIdx < womenImages.length)) {
                      if (useMan && menIdx < menImages.length) {
                        alternatingImages.push(menImages[menIdx++]);
                      } else if (!useMan && womenIdx < womenImages.length) {
                        alternatingImages.push(womenImages[womenIdx++]);
                      }
                      useMan = !useMan;
                    }
                  }
                  // --- END: ALTERNATING LOGIC ---
                  setImages(alternatingImages);
                  // Initialize folder tracking for each image
                  const initialFolders = {};
                  alternatingImages.forEach(img => {
                    initialFolders[img.uniqueId] = img.subfolder;
                  });
                  setImageFolders(initialFolders);
                  // Initialize current displayed images for each cell
                  const initialDisplayedImages = {};
                  const initialCellCodes = {};
                  for (let i = 0; i < GRID_CONFIG.rows * GRID_CONFIG.cols; i++) {
                    const imageIndex = i % alternatingImages.length;
                    const image = alternatingImages[imageIndex];
                    if (image) {
                      initialDisplayedImages[`cell_${i}`] = {
                        subfolder: image.subfolder,
                        number: image.number
                      };
                      initialCellCodes[`cell_${i}`] = generateHashCode(image.path);
                    }
                  }
                  setCurrentDisplayedImages(initialDisplayedImages);
                  setCellCodes(initialCellCodes);
                  // Place 3 random 2x2 large blocks
                  const totalCells = GRID_CONFIG.rows * GRID_CONFIG.cols;
                  const validBlockPositions = [];
                  for (let row = 0; row < GRID_CONFIG.rows - 1; row++) {
                    for (let col = 0; col < GRID_CONFIG.cols - 1; col++) {
                      validBlockPositions.push(row * GRID_CONFIG.cols + col);
                    }
                  }
                  // Create 3 large blocks with non-overlapping positions
                  const largeBlocks = [];
                  const usedPositions = new Set();
                  const usedImageNumbers = new Set(); // Track used image numbers
                  let blockManIdx = 0, blockWomanIdx = 0, blockUseMan = true;
                  for (let i = 0; i < 3; i++) {
                    // Filter out positions that would overlap with existing blocks
                    const availablePositions = validBlockPositions.filter(pos => {
                      const row = Math.floor(pos / GRID_CONFIG.cols);
                      const col = pos % GRID_CONFIG.cols;
                      const adjacentPositions = [
                        pos, // top-left
                        pos + 1, // top-right
                        pos + GRID_CONFIG.cols, // bottom-left
                        pos + GRID_CONFIG.cols + 1 // bottom-right
                      ];
                      return !adjacentPositions.some(p => usedPositions.has(p));
                    });
                    if (availablePositions.length === 0) {
                      break;
                    }
                    const randomPosition = availablePositions[Math.floor(Math.random() * availablePositions.length)];
                    // Alternate man/woman for large blocks
                    let blockImage = null;
                    if (blockUseMan && blockManIdx < menImages.length) {
                      // Find next unused man image
                      while (blockManIdx < menImages.length && usedImageNumbers.has(menImages[blockManIdx].number)) blockManIdx++;
                      if (blockManIdx < menImages.length) {
                        blockImage = menImages[blockManIdx++];
                      }
                    } else if (!blockUseMan && blockWomanIdx < womenImages.length) {
                      // Find next unused woman image
                      while (blockWomanIdx < womenImages.length && usedImageNumbers.has(womenImages[blockWomanIdx].number)) blockWomanIdx++;
                      if (blockWomanIdx < womenImages.length) {
                        blockImage = womenImages[blockWomanIdx++];
                      }
                    }
                    // If ran out, fallback to any available
                    if (!blockImage) {
                      const availableImages = alternatingImages.filter(img => !usedImageNumbers.has(img.number));
                      if (availableImages.length > 0) {
                        blockImage = availableImages[Math.floor(Math.random() * availableImages.length)];
                      } else {
                        blockImage = alternatingImages[Math.floor(Math.random() * alternatingImages.length)];
                      }
                    }
                    usedImageNumbers.add(blockImage.number);
                    const blockRow = Math.floor(randomPosition / GRID_CONFIG.cols);
                    const blockCol = randomPosition % GRID_CONFIG.cols;
                    const blockPositions = [
                      randomPosition,
                      randomPosition + 1,
                      randomPosition + GRID_CONFIG.cols,
                      randomPosition + GRID_CONFIG.cols + 1
                    ];
                    blockPositions.forEach(pos => usedPositions.add(pos));
                    largeBlocks.push({
                      topLeft: randomPosition,
                      image: blockImage,
                      positions: blockPositions,
                      blockId: i
                    });
                    blockUseMan = !blockUseMan;
                  }
                  setLargeBlock(largeBlocks);
                  // Initialize large block codes
                  const initialLargeBlockCodes = {};
                  largeBlocks.forEach(block => {
                    initialLargeBlockCodes[`large_block_${block.topLeft}`] = generateHashCode(block.image.path);
                  });
                  setLargeBlockCodes(initialLargeBlockCodes);
                  setIsLoading(false);
                }
              };
              img.onerror = () => {
                console.error(`Failed to load image: ${imagePath}`);
              };
              img.src = imagePath;
            });
          });
        });
      } catch (error) {
        console.error('Error loading images:', error);
        setIsLoading(false);
      }
    };
    loadImages();
  }, []);

  // Get next folder in cycle - dynamically detect available folders for specific top folder
  const getNextFolder = (currentFolder, topFolder) => {
    // Get all available subfolders for this specific top folder in the order they were loaded
    const availableFolders = [...new Set(
      images
        .filter(img => img.topFolder === topFolder)
        .map(img => img.subfolder)
    )];
    
    if (availableFolders.length === 0) {
      console.warn(`No folders available for cycling in ${topFolder}`);
      return currentFolder;
    }
    
    const currentIndex = availableFolders.indexOf(currentFolder);
    const nextIndex = (currentIndex + 1) % availableFolders.length;
    return availableFolders[nextIndex];
  };

  // ANIMATE_THIS_FUCKING_IMAGE function - now takes cellId
  const ANIMATE_THIS_FUCKING_IMAGE = (cellId) => {
    if (animations[cellId]) {
      return; // Don't animate if already animating
    }

    // Check if this is a large block
    if (cellId.startsWith('large_block_')) {
      const clickedPosition = parseInt(cellId.split('_')[2]);
      
      // Find which block contains this position
      const block = largeBlock.find(b => b.positions.includes(clickedPosition));
      if (!block) {
        console.error(`No block found for position ${clickedPosition}`);
        return;
      }
      
      const currentImage = block.image;
      
      if (!currentImage) {
        return;
      }

      // Get next folder in cycle - EXACTLY like small cells
      const nextFolder = getNextFolder(currentImage.subfolder, currentImage.topFolder);
      const imageFileName = currentImage.number;
      
      console.log(`Large block animation: ${currentImage.topFolder}/${nextFolder}/${imageFileName}.avif (number: ${currentImage.number})`);

      // Load the next image
      const nextImg = new Image();
      nextImg.crossOrigin = 'anonymous';
      nextImg.onload = () => {
        // Start the animation
        setAnimations(prev => ({
          ...prev,
          [cellId]: {
            progress: 0,
            nextImage: nextImg,
            startTime: performance.now(),
            currentImageId: currentImage.imageId
          }
        }));
      };
      nextImg.onerror = () => {
        console.error(`Failed to load ${currentImage.topFolder}/${nextFolder}/${imageFileName}.avif`);
      };
      nextImg.src = `/img/complete_grid/${currentImage.topFolder}/${nextFolder}/${imageFileName}.avif`;
      return;
    }

    // Regular cell animation
    const gridIndex = parseInt(cellId.split('_')[1]);
    const imageIndex = gridIndex % images.length;
    const currentImage = images[imageIndex];
    
    if (!currentImage) {
      return;
    }

    // Get next folder in cycle
    const nextFolder = getNextFolder(currentImage.subfolder, currentImage.topFolder);
    const imageFileName = currentImage.number;

    // Load the next image
    const nextImg = new Image();
    nextImg.crossOrigin = 'anonymous';
    nextImg.onload = () => {
      // Start the animation
      setAnimations(prev => ({
        ...prev,
        [cellId]: {
          progress: 0,
          nextImage: nextImg,
          startTime: performance.now(),
          currentImageId: currentImage.imageId
        }
      }));
    };
    nextImg.onerror = () => {
      console.error(`Failed to load ${currentImage.topFolder}/${nextFolder}/${imageFileName}.avif`);
    };
    nextImg.src = `/img/complete_grid/${currentImage.topFolder}/${nextFolder}/${imageFileName}.avif`;
  };

  // Random animation function
  const RANDOMLY_ANIMATE_AN_IMAGE = () => {
    const totalCells = GRID_CONFIG.rows * GRID_CONFIG.cols;
    const availableCells = [];
    
    // Get all cells that aren't part of the large block
    for (let i = 0; i < totalCells; i++) {
      const cellId = `cell_${i}`;
      if (!animations[cellId] && (!largeBlock || !largeBlock.some(block => block.positions.includes(i)))) {
        availableCells.push(cellId);
      }
    }
    
    if (availableCells.length > 0) {
      const randomCellId = availableCells[Math.floor(Math.random() * availableCells.length)];
      ANIMATE_THIS_FUCKING_IMAGE(randomCellId);
    }
  };

  // Automatic random animation
  useEffect(() => {
    if (!ANIMATION_CONFIG.enabled || images.length === 0) {
      return;
    }
    
    const animationInterval = setInterval(() => {
      RANDOMLY_ANIMATE_AN_IMAGE();
    }, ANIMATION_CONFIG.frequency);

    return () => {
      clearInterval(animationInterval);
    };
  }, [images]);

  // Animation loop - handles multiple animations simultaneously
  useEffect(() => {
    let rafId;
    const totalDuration = ANIMATION_CONFIG.speed;

    function animate() {
      setAnimations(prev => {
        const now = performance.now();
        const updated = { ...prev };
        const completed = [];

        // Update progress for all active animations
        Object.keys(updated).forEach(cellId => {
          const anim = updated[cellId];
          const elapsed = now - anim.startTime;
          const progress = Math.min(elapsed / totalDuration, 1);
          
          updated[cellId] = {
            ...anim,
            progress
          };

          if (progress >= 1) {
            completed.push(cellId);
          }
        });

        // Handle completed animations
        completed.forEach(uniqueId => {
          const anim = updated[uniqueId];
          
          // Check if this is a hover animation
          if (anim.isHover) {
            // For hover animations, just clean them up WITHOUT updating the base image
            // This way the image reverts back to the original after the animation
            delete updated[uniqueId];
            return;
          }
          
          // Check if this is a large block animation
          if (uniqueId.startsWith('large_block_')) {
            const topLeft = parseInt(uniqueId.split('_')[2]);
            const blockIndex = largeBlock.findIndex(b => b.topLeft === topLeft);
            if (blockIndex !== -1) {
              setLargeBlock(prev => prev.map((b, i) =>
                i === blockIndex
                  ? { ...b, image: { ...b.image, img: anim.nextImage, subfolder: getNextFolder(b.image.subfolder, b.image.topFolder) } }
                  : b
              ));
              setCurrentDisplayedImages(prev => ({
                ...prev,
                [uniqueId]: {
                  subfolder: getNextFolder(largeBlock[blockIndex].image.subfolder, largeBlock[blockIndex].image.topFolder),
                  number: largeBlock[blockIndex].image.number
                }
              }));
              setRenderCounter(prev => prev + 1);
            }
            delete updated[uniqueId];
            return;
          }
          
          // Regular cell animation
          const gridIndex = parseInt(uniqueId.split('_')[1]);
          const imageIndex = gridIndex % images.length;
          const currentImage = images[imageIndex];
          
          if (currentImage) {
            // Update the image in the images array
            setImages(prev => prev.map(img => 
              img.imageId === anim.currentImageId 
                ? { ...img, img: anim.nextImage, subfolder: getNextFolder(img.subfolder, img.topFolder) }
                : img
            ));
            
            // Update the current displayed image for this cell
            setCurrentDisplayedImages(prev => ({
              ...prev,
              [uniqueId]: {
                subfolder: getNextFolder(currentImage.subfolder, currentImage.topFolder),
                number: currentImage.number
              }
            }));
            
            // Force re-render
            setRenderCounter(prev => prev + 1);
          }
          
          delete updated[uniqueId];
        });

        return updated;
      });

      rafId = requestAnimationFrame(animate);
    }

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [images, animations, largeBlock, lastClick, renderCounter, currentDisplayedImages]);

  // Draw pixelated image function
  const drawPixelatedImage = (ctx, img, x, y, width, height, pixelSize) => {
    if (pixelSize <= 1) {
      // Draw image at natural size
      ctx.drawImage(img, x, y, width, height);
      return;
    }

    // For pixelated version, use temp canvas
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    const pixelWidth = Math.ceil(width / pixelSize);
    const pixelHeight = Math.ceil(height / pixelSize);
    tempCanvas.width = pixelWidth;
    tempCanvas.height = pixelHeight;

    tempCtx.drawImage(img, 0, 0, pixelWidth, pixelHeight);

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tempCanvas, x, y, width, height);
    ctx.imageSmoothingEnabled = true;
  };

  // Set canvas dimensions
  useEffect(() => {
    if (canvasRef.current) {
      const containerWidth = Math.min(windowWidth, 1200); // Max width of 1200px
      const imageRatio = 10 / 12; // 10:12 ratio as specified
      const cellHeight = containerWidth / GRID_CONFIG.cols / imageRatio;
      const canvasHeight = cellHeight * GRID_CONFIG.rows;
      
      canvasRef.current.width = containerWidth;
      canvasRef.current.height = canvasHeight;
    }
  }, [windowWidth, GRID_CONFIG.cols]);

  // Draw the grid on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || images.length === 0) return;

    const ctx = canvas.getContext('2d');
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

    // Clear canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const { rows, cols, gap } = GRID_CONFIG;
    const imageRatio = 10 / 12; // 10:12 ratio
    const cellWidth = canvasWidth / cols;
    const cellHeight = cellWidth / imageRatio; // Maintain 10:12 ratio

    // Draw grid cells
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const gridIndex = row * cols + col;
        const x = col * cellWidth;
        const y = row * cellHeight;
        const cellId = `cell_${gridIndex}`; // Cell-based unique identifier
        
        // Check if this cell is part of the large block
        const isInLargeBlock = largeBlock && largeBlock.some(block => block.positions.includes(gridIndex));
        
        if (!isInLargeBlock) {
          // Assign each cell a unique image from the sequence
          const imageIndex = gridIndex % images.length;
          const image = images[imageIndex];
          
          if (image && image.img) {
            const animation = animations[cellId];
            const hoverAnimation = animations[`hover_${cellId}`];
            
            if (animation) {
              // Regular animation stages
              if (animation.progress < 0.5) {
                // Stage 1: Pixelate current image
                const pixelationProgress = animation.progress / 0.5;
                const pixelSize = PIXELATION_CONFIG.minPixelSize + Math.floor(pixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
                drawPixelatedImage(ctx, image.img, x, y, cellWidth, cellHeight, pixelSize);
              } else {
                // Stage 2: Unpixelate next image
                if (animation.nextImage) {
                  const unpixelationProgress = (animation.progress - 0.5) / 0.5;
                  const pixelSize = PIXELATION_CONFIG.maxPixelSize - Math.floor(unpixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
                  drawPixelatedImage(ctx, animation.nextImage, x, y, cellWidth, cellHeight, pixelSize);
                }
              }
            } else if (hoverAnimation) {
              // Hover animation stages
              if (hoverAnimation.progress < 0.5) {
                // Stage 1: Pixelate current image
                const pixelationProgress = hoverAnimation.progress / 0.5;
                const pixelSize = PIXELATION_CONFIG.minPixelSize + Math.floor(pixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
                drawPixelatedImage(ctx, image.img, x, y, cellWidth, cellHeight, pixelSize);
              } else {
                // Stage 2: Unpixelate next image
                if (hoverAnimation.nextImage) {
                  const unpixelationProgress = (hoverAnimation.progress - 0.5) / 0.5;
                  const pixelSize = PIXELATION_CONFIG.maxPixelSize - Math.floor(unpixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
                  drawPixelatedImage(ctx, hoverAnimation.nextImage, x, y, cellWidth, cellHeight, pixelSize);
                }
              }
            } else {
              // Normal drawing - draw image at natural size to fill cell
              ctx.drawImage(image.img, x, y, cellWidth, cellHeight);
            }
            
            // Debug: Show cell ID and image info
            if (DEBUG_CONFIG.showDebugText) {
              ctx.fillStyle = DEBUG_CONFIG.debugTextColor;
              ctx.font = '8px var(--font-b)';
              const debugLineHeight = 10;
              let debugY = y + cellHeight - 4;
              
              // Get the current displayed image info for this cell
              const displayedImage = currentDisplayedImages[cellId];
              let actualSubfolder = image.subfolder;
              let actualNumber = image.number;
              
              if (displayedImage) {
                actualSubfolder = displayedImage.subfolder;
                actualNumber = displayedImage.number;
              } else if (animation && animation.progress >= 0.5 && animation.nextImage) {
                // During unpixelation phase, we're drawing the next image
                actualSubfolder = getNextFolder(image.subfolder, image.topFolder);
              } else if (hoverAnimation && hoverAnimation.progress >= 0.5 && hoverAnimation.nextImage) {
                // During hover unpixelation phase, we're drawing the next image
                actualSubfolder = getNextFolder(image.subfolder, image.topFolder);
              }
              
              // Ensure code exists for this cell
              let code = cellCodes[cellId];
              if (!code) {
                code = generateHashCode(image.path);
                setCellCodes(prev => ({ ...prev, [cellId]: code }));
              }
              ctx.fillText(`avt:${actualSubfolder}_${actualNumber}`, x + 2, debugY);
              debugY -= debugLineHeight;
              ctx.fillText(`idx:${code}`, x + 2, debugY);
            }
            
            // Draw random glyph in top right corner
            const glyphSize = GLYPH_CONFIG.size;
            const glyphX = x + cellWidth - glyphSize - GLYPH_CONFIG.padding;
            const glyphY = y + GLYPH_CONFIG.padding;
            
            // Get random g01-g05
            const randomGlyph = getCyclingG01(imageIndex);
            
            // Draw the actual glyph with proper scaling
            if (randomGlyph) {
              const path = new Path2D(randomGlyph);
              ctx.save();
              ctx.translate(glyphX, glyphY);
              ctx.scale(glyphSize / GLYPH_CONFIG.scale, glyphSize / GLYPH_CONFIG.scale); // Scale from 24x24 to glyphSize
              ctx.fillStyle = 'white';
              ctx.fill(path);
              ctx.restore();
            }
          }
        } else {
          // Debug: Show large block cells
          ctx.fillStyle = 'rgba(53, 32, 214, 1)'; // Using #3520D6 with full opacity
          ctx.fillRect(x, y, cellWidth, cellHeight);
          
          // Create pattern fill with the glyph at 14x14 size
          const patternSize = 14;
          const patternCanvas = document.createElement('canvas');
          const patternCtx = patternCanvas.getContext('2d');
          patternCanvas.width = patternSize;
          patternCanvas.height = patternSize;
          
          // Get the glyph for this cell index
          const randomGlyph = getCyclingG01(gridIndex);
          
          // Draw the glyph in white on the pattern canvas
          if (randomGlyph) {
            const path = new Path2D(randomGlyph);
            patternCtx.save();
            patternCtx.translate(patternSize / 2, patternSize / 2);
            patternCtx.scale(patternSize / GLYPH_CONFIG.scale, patternSize / GLYPH_CONFIG.scale);
            patternCtx.translate(-GLYPH_CONFIG.scale / 2, -GLYPH_CONFIG.scale / 2);
            patternCtx.fillStyle = 'white';
            patternCtx.fill(path);
            patternCtx.restore();
          }
          
          // Create pattern and fill the cell
          const pattern = ctx.createPattern(patternCanvas, 'repeat');
          ctx.fillStyle = pattern;
          ctx.fillRect(x, y, cellWidth, cellHeight);
        }
      }
    }

    // Draw large blocks
    largeBlock.forEach((block, index) => {
      const { topLeft, image, positions } = block;
      const blockRow = Math.floor(topLeft / cols);
      const blockCol = topLeft % cols;
      const x = blockCol * cellWidth;
      const y = blockRow * cellHeight;
      const width = cellWidth * 2;
      const height = cellHeight * 2;
      
      // Debug: Show large block info
      // (border removed)
      const largeBlockId = `large_block_${topLeft}`;
      const animation = animations[largeBlockId];
      const hoverAnimation = animations[`hover_${largeBlockId}`];
      
      if (animation) {
        // Regular animation stages for large block
        if (animation.progress < 0.5) {
          const pixelationProgress = animation.progress / 0.5;
          const pixelSize = PIXELATION_CONFIG.minPixelSize + Math.floor(pixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
          drawPixelatedImage(ctx, image.img, x, y, width, height, pixelSize);
            } else {
          if (animation.nextImage) {
            const unpixelationProgress = (animation.progress - 0.5) / 0.5;
            const pixelSize = PIXELATION_CONFIG.maxPixelSize - Math.floor(unpixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
            drawPixelatedImage(ctx, animation.nextImage, x, y, width, height, pixelSize);
          }
        }
      } else if (hoverAnimation) {
        // Hover animation stages for large block
        if (hoverAnimation.progress < 0.5) {
          const pixelationProgress = hoverAnimation.progress / 0.5;
          const pixelSize = PIXELATION_CONFIG.minPixelSize + Math.floor(pixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
          drawPixelatedImage(ctx, image.img, x, y, width, height, pixelSize);
          } else {
          if (hoverAnimation.nextImage) {
            const unpixelationProgress = (hoverAnimation.progress - 0.5) / 0.5;
            const pixelSize = PIXELATION_CONFIG.maxPixelSize - Math.floor(unpixelationProgress * PIXELATION_CONFIG.pixelSteps) * PIXELATION_CONFIG.pixelStepSize;
            drawPixelatedImage(ctx, hoverAnimation.nextImage, x, y, width, height, pixelSize);
          }
        }
      } else {
        // Normal drawing for large block
        ctx.drawImage(image.img, x, y, width, height);
      }
      
      // Debug: Show large block ID
      if (DEBUG_CONFIG.showDebugText) {
        ctx.fillStyle = DEBUG_CONFIG.debugTextColor;
        ctx.font = '12px Roboto Mono, monospace';
        const blockDebugLineHeight = 14;
        let blockDebugY = y + height - 6;
        
        // Get the current displayed image info for this large block
        const displayedImage = currentDisplayedImages[largeBlockId];
        let actualSubfolder = image.subfolder;
        let actualNumber = image.number;
        
        if (displayedImage) {
          actualSubfolder = displayedImage.subfolder;
          actualNumber = displayedImage.number;
        } else if (animation && animation.progress >= 0.5 && animation.nextImage) {
          // During unpixelation phase, we're drawing the next image
          actualSubfolder = getNextFolder(image.subfolder, image.topFolder);
        } else if (hoverAnimation && hoverAnimation.progress >= 0.5 && hoverAnimation.nextImage) {
          // During hover unpixelation phase, we're drawing the next image
          actualSubfolder = getNextFolder(image.subfolder, image.topFolder);
        }
        
        // Ensure code exists for this cell
        let code = largeBlockCodes[largeBlockId];
        if (!code) {
          code = generateHashCode(image.path);
          setLargeBlockCodes(prev => ({ ...prev, [largeBlockId]: code }));
        }
        ctx.fillText(`avt:${actualSubfolder}_${actualNumber}`, x + 5, blockDebugY);
        blockDebugY -= blockDebugLineHeight;
        ctx.fillText(`idx:${code}`, x + 5, blockDebugY);
      }
      
      // Draw random glyph in top right corner
      const glyphSize = GLYPH_CONFIG.size;
      const glyphX = x + width - glyphSize - GLYPH_CONFIG.padding;
      const glyphY = y + GLYPH_CONFIG.padding;
      
      // Get random g01-g05
      const randomGlyph = getCyclingG01(index);
      
      // Draw the actual glyph with proper scaling
      if (randomGlyph) {
        const path = new Path2D(randomGlyph);
        ctx.save();
        ctx.translate(glyphX, glyphY);
        ctx.scale(glyphSize / GLYPH_CONFIG.scale, glyphSize / GLYPH_CONFIG.scale); // Scale from 24x24 to glyphSize
        ctx.fillStyle = 'white';
        ctx.fill(path);
        ctx.restore();
      }
    });
    
    // Debug: Show last click position
    if (lastClick && DEBUG_CONFIG.showClickIndicator) {
      // Draw g01-style rectangle at click position
      const glyphSize = 20;
      const glyphX = lastClick.x - glyphSize / 2;
      const glyphY = lastClick.y - glyphSize / 2;
      
      // Use CSS variable --c5 for the click indicator
      ctx.fillStyle = DEBUG_CONFIG.clickIndicatorColor;
      ctx.strokeStyle = DEBUG_CONFIG.clickIndicatorColor;
      ctx.lineWidth = 2;
      
      // Draw outer rectangle (like g01)
      ctx.strokeRect(glyphX, glyphY, glyphSize, glyphSize);
      
      // Draw inner rectangle (like g01 center)
      const innerSize = glyphSize * 0.6;
      const innerX = glyphX + (glyphSize - innerSize) / 2;
      const innerY = glyphY + (glyphSize - innerSize) / 2;
      ctx.fillRect(innerX, innerY, innerSize, innerSize);
      
      ctx.fillStyle = DEBUG_CONFIG.clickIndicatorColor;
      ctx.font = '10px var(--font-b)';
      ctx.fillText(`Click: (${Math.round(lastClick.x)}, ${Math.round(lastClick.y)})`, lastClick.x + 15, lastClick.y - 10);
    }
  }, [images, animations, largeBlock, lastClick, renderCounter, currentDisplayedImages]);

  // Click handler - properly detects which cell was clicked with correct scaling
  const handleClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const canvas = canvasRef.current;
    
    // Get the actual canvas dimensions vs displayed dimensions
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Scale the click coordinates to match canvas internal coordinates
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const { rows, cols } = GRID_CONFIG;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const imageRatio = 10 / 12;
    const cellWidth = canvasWidth / cols;
    const cellHeight = cellWidth / imageRatio;
    
    // Calculate which cell was clicked
    const col = Math.floor(x / cellWidth);
    const row = Math.floor(y / cellHeight);
    
    console.log(`Raw click: (${e.clientX - rect.left}, ${e.clientY - rect.top})`);
    console.log(`Scaled click: (${x}, ${y})`);
    console.log(`Canvas size: ${canvasWidth}x${canvasHeight}, Display size: ${rect.width}x${rect.height}`);
    console.log(`Cell size: ${cellWidth}x${cellHeight}`);
    console.log(`Click at (${x}, ${y}) -> Cell (${row}, ${col})`); // Debug
    
    if (col >= 0 && col < cols && row >= 0 && row < rows) {
      const gridIndex = row * cols + col;
      const cellId = `cell_${gridIndex}`;
      
      console.log(`Grid index: ${gridIndex}, Cell ID: ${cellId}`); // Debug
      
      // Check if this cell is part of the large block
      if (largeBlock && largeBlock.some(block => block.positions.includes(gridIndex))) {
        console.log(`Large block clicked! Animating large block`); // Debug
        // Find which block this position belongs to
        const clickedBlock = largeBlock.find(block => block.positions.includes(gridIndex));
        const largeBlockId = `large_block_${clickedBlock.topLeft}`;
        ANIMATE_THIS_FUCKING_IMAGE(largeBlockId);
      } else {
        console.log(`Animating cell: ${cellId}`); // Debug
        ANIMATE_THIS_FUCKING_IMAGE(cellId);
      }
    } else {
      console.log(`Click outside grid bounds: (${col}, ${row})`); // Debug
    }

    // Update last click for debugging
    setLastClick({ x, y });
  };

  // Hover animation function
  const handleMouseMove = (e) => {
    if (!hoverEnabled) return; // Only animate on hover if enabled
    
    const rect = canvasRef.current.getBoundingClientRect();
    const canvas = canvasRef.current;
    
    // Get the actual canvas dimensions vs displayed dimensions
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Scale the hover coordinates to match canvas internal coordinates
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const { rows, cols } = GRID_CONFIG;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const imageRatio = 10 / 12;
    const cellWidth = canvasWidth / cols;
    const cellHeight = cellWidth / imageRatio;
    
    // Calculate which cell was hovered
    const col = Math.floor(x / cellWidth);
    const row = Math.floor(y / cellHeight);
    
    if (col >= 0 && col < cols && row >= 0 && row < rows) {
      const gridIndex = row * cols + col;
      const cellId = `cell_${gridIndex}`;
      
      // Check if we've moved to a new cell
      if (currentHoveredCell !== cellId) {
        setCurrentHoveredCell(cellId);
        
        // Reset hovered cells when moving to a new cell
        setHoveredCells(new Set());
        
        console.log(`Hover over cell: ${cellId}`); // Debug
        
        // Check if this cell is part of a large block
        if (largeBlock && largeBlock.some(block => block.positions.includes(gridIndex))) {
          const clickedBlock = largeBlock.find(block => block.positions.includes(gridIndex));
          const largeBlockId = `large_block_${clickedBlock.topLeft}`;
          console.log(`Hover over large block: ${largeBlockId}`); // Debug
          
          // Only trigger if we haven't hovered this block yet
          if (!hoveredCells.has(largeBlockId)) {
            setHoveredCells(prev => new Set([...prev, largeBlockId]));
            const hoverAnimationId = `hover_${largeBlockId}`;
            triggerHoverAnimation(largeBlockId, hoverAnimationId);
          }
        } else {
          console.log(`Hover animation for cell: ${cellId}`); // Debug
          
          // Only trigger if we haven't hovered this cell yet
          if (!hoveredCells.has(cellId)) {
            setHoveredCells(prev => new Set([...prev, cellId]));
            const hoverAnimationId = `hover_${cellId}`;
            triggerHoverAnimation(cellId, hoverAnimationId);
          }
        }
      }
    } else {
      // Mouse is outside grid bounds
      setCurrentHoveredCell(null);
    }
  };

  // Separate hover animation trigger function - EXACTLY the same as click
  const triggerHoverAnimation = (originalCellId, hoverAnimationId) => {
    // Check if a hover animation is already running for this cell/block
    if (animations[hoverAnimationId]) {
      return; // Don't start another hover animation if one is already running
    }
    
    // Use the EXACT same animation logic as click, just with hover key
    // Temporarily replace the animation key to use hover prefix
    const originalAnimations = { ...animations };
    
    // Call the same function that handles click animations
    ANIMATE_THIS_FUCKING_IMAGE(originalCellId);
    
    // If an animation was started, rename it to use hover prefix
    setAnimations(prev => {
      const updated = { ...prev };
      if (updated[originalCellId]) {
        // Move the animation to the hover key and mark it as hover
        updated[hoverAnimationId] = {
          ...updated[originalCellId],
          isHover: true
        };
        delete updated[originalCellId];
      }
      return updated;
    });
  };

  // Mouse leave handler - reset hover state and clear hover animations
  const handleMouseLeave = (e) => {
    console.log('Mouse left canvas area');
    setCurrentHoveredCell(null);
    setHoveredCells(new Set());
    
    // Clear all hover animations
    setAnimations(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (key.startsWith('hover_')) {
          delete updated[key];
        }
      });
      return updated;
    });
  };

  // Get cycling g01-g05 function
  const getCyclingG01 = (imageIndex) => {
    const glyphIndex = (imageIndex % 5) + 1; // Use image index to assign different glyph to each image
    const glyphKey = `g0${glyphIndex}`;
    const svgPath = glyphPaths[glyphKey];
    // Extract the path data from the SVG path string
    const pathMatch = svgPath.match(/d="([^"]+)"/);
    return pathMatch ? pathMatch[1] : '';
  };

  return (
    <>
    {isLoading && (
      <div className="text-lg text-gray-600 mb-4">Loading images...</div>
    )}

    <div className="rounded overflow-hidden shadow-sm">
      <canvas
        ref={canvasRef}
        className="block w-full cursor-pointer"
        style={{ 
          maxWidth: '100%',
          display: 'block'
        }}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
    </div>
    </>
  );
};

export default ImageGridCanvas;